import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { InsertDriveFileOutlined } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-IN") : null;

/*
 * Compact summary of the data currently stored for a report:
 * which file, when, by whom, and a few counts. The rows themselves
 * are only rendered on demand, because reports can have thousands of rows.
 */
const LatestUploadCard = ({
  title = "Currently Uploaded",
  fileName,
  uploadedAt,
  uploadedAtLabel = "Uploaded on",
  uploadedBy,
  stats = [],
  loading,
  error,
  emptyMessage = "Nothing has been uploaded yet.",
  isEmpty,
  onRefresh,
  rows = [],
  columns = [],
}) => {
  const [viewOpen, setViewOpen] = useState(false);

  return (
    <Paper
      elevation={0}
      sx={{ border: "1px solid #e3e7ef", borderRadius: 2, p: 2.5 }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <InsertDriveFileOutlined sx={{ color: "text.secondary" }} />
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
            {!loading && !error && !isEmpty && (
              <Typography variant="body2" color="text.secondary">
                {[
                  fileName,
                  uploadedAt &&
                    `${uploadedAtLabel} ${formatDateTime(uploadedAt)}`,
                  uploadedBy && `by ${uploadedBy}`,
                ]
                  .filter(Boolean)
                  .join(" · ") || "Details of the stored data"}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : loading ? null : isEmpty ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {emptyMessage}
        </Typography>
      ) : (
        stats.length > 0 && (
          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: `repeat(${Math.min(stats.length, 4)}, minmax(0, 1fr))`,
              },
              gap: 1.5,
            }}
          >
            {stats.map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  border: "1px solid #eef1f6",
                  borderRadius: 1.5,
                  backgroundColor: "#f7f9fc",
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {stat.label}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
                  {typeof stat.value === "number"
                    ? stat.value.toLocaleString("en-IN")
                    : stat.value ?? "—"}
                </Typography>
              </Box>
            ))}
          </Box>
        )
      )}

      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>{fileName || title}</DialogTitle>
        <DialogContent>
          {viewOpen && (
            <DataGrid
              rows={rows}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f7f9fc",
                  fontWeight: 700,
                },
                "& .MuiDataGrid-cell": { fontSize: "0.82rem" },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LatestUploadCard;
